import React from 'react';
import Immutable from 'immutable';
import {
	styled,
	ParentBoundComponent,
	ResultSetModel,
	Icon,
	Pager,
	FlexBox,
	FlexBoxBlock,
	Button,
	TextInput,
	LookupInput,
	PopupInput,
	RuntimeRangeInput,
	NumberRangeInput,
	TextInput2,
	DateRangeInput,
} from 'ps-gui';

import {
	getColumnText,
	getPhrase
} from '../data/phrases';
import { exportToCsv } from './utils';
import { getLookup } from '../data/lookup';

const Wrapper = styled.div`
	.ps-enumPager {
		> .ps-enumPage:first-child {
			margin-left: 0px;
		}
		> .ps-enumPageGroup:first-child {
			> .ps-enumPage:first-child {
				margin-left: 0px;
			}
		}

		.next, .prev {
			color: ${props => props.theme.lightButtonColor};
			text-transform: uppercase;
		}
	}
`;

export const SummaryHeaderRowCount = styled.span`
	.label {
		font-size: 14px;
		font-weight: 500;
	}
	.value {
		margin-left: 4px;
		font-size: 16px;
		font-weight: 500;
	}
`;

export const SummaryHeaderMainText = styled.div`
	font-weight: 500;
	font-size: 14px;
	line-height: 22px;
	text-align: ${props => props.textAlign || 'right'};
`;

export const SummaryHeaderSubText = styled.div`
	font-weight: 400;
	font-size: 14px;
	line-height: 20px;
	text-align: ${props => props.textAlign || 'right'};
`;

export const CellSubText = styled.div`
	font-size: 14px;
	margin-top: 4px;
	color: ${props => props.theme.disabledTextColor};
`;


export const CellFiller = styled.div`
	position: relative;
	width: 100%;
	height: 100%;
`;

export const CellMainText = styled.div`
	color: ${props => props.theme.textColor};
	&.secondary {
		color: ${props => props.theme.disabledTextColor};
	}
	&.ok {
		color: ${props => props.theme.okColor};
	}
	&.error {
		color: ${props => props.theme.errorColor};
	}
	&.warning {
		color: ${props => props.theme.warningColor};
	}
	&.deleted {
		text-decoration: line-through;
	}
	font-size: 14px;
	font-weight: 500;
`;

export const CellBubble = styled.span`
	font-size: 14px;
	font-weight: 500;
	border-radius: 16px;
	padding: 4px 16px;
	&.IP {
		color: #306DA0;
		background: #D1F1E6;
	}

	&.NP {
		color: #5C2775;
		background: #F6E3FF;
	}
	&.usageReportFlag_R {
		color: #5C2775;
		background: #F6E3FF;
	}
	&.usageReportFlag_P {
		color: #306DA0;
		background: #D1F1E6;
	}

	&.recordingTypeA {
		color: ${props => props.theme.recordingTypeAColor};
		background: ${props => props.theme.bcgRecordingTypeA};
	}
	&.recordingTypeV {
		color: ${props => props.theme.recordingTypeVColor};
		background: ${props => props.theme.bcgRecordingTypeV};
	}
	&.recordingTypeOther {
		color: ${props => props.theme.recordingTypeOtherColor};
		background: ${props => props.theme.bcgRecordingTypeOther};
	}
`;

const RowsPerPageSelection = styled.div`
	margin-top: 40px;
	.label {
		display: inline-block;
		font-size: 14px;
		font-weight: 500;
		color: ${props => props.theme.tableCellTextColor};
		border-bottom: 2px solid transparent;
	}
	.value {
		width: 20px;
		text-align: center;
		font-weight: 500;
		font-size: 14px;
		margin-left: 24px;
		border-bottom: 2px solid transparent;
		&.selected {
			font-weight: 900;
			border-bottom-color: ${props => props.theme.activeColor};
		}
		&:not(.selected) {
			cursor: pointer;
			&:hover {
				font-weight: 900;
			}
		}
	}
`;

const SmartTable = styled.table`
	width: 100%;
	z-index: 0;
	border-spacing: 0px;
	table-layout: fixed;
	td, th {
		&.right {
			text-align: right !important;
		}
	}
	thead {
		tr {
			&:first-child {
				th:first-child {
					border-top-left-radius: 15px;
				}
				th:last-child {
					border-top-right-radius: 15px;
				}
			}

			&.dummyHeaderRow {
				height: 8px;
				th {
					padding: 0px;
					border: none;
					background: #EAF0F2;
				}
			}

			th {
				z-index: 3;
				text-align: left;
				padding: 12px;
				background: ${props => props.theme.tableHeaderBackground};
				border-right: 1px solid ${props => props.theme.tableCellBorderColor};
				//border-bottom: 1px solid ${props => props.theme.tableCellBorderColor};
				box-shadow: ${props => props.theme.tableHeaderShadow};
				color: ${props => props.theme.tableHeaderTextColor};
				.text {
					font-size: 14px;
					font-weight: 500;
					z-index: 1;
					position: relative;
					padding-right: 16px;
				}
				.subText {
					color: ${props => props.theme.disabledTextColor};
					font-size: 14px;
					font-weight: 300;
					position: relative;
				}
				&.sortable {
					> .text {
						cursor: pointer;
						&:hover {
							text-decoration: underline;
						}
					}
				}

					.sortIcons {
						position: absolute;
						right: 0px;
						top: calc( 50% - 10px );

						.icon.selected {
							* {
								fill: ${props => props.theme.activeColor};
							}
						}
					}
			}
			&.sticky {
				th {
					position: sticky;
					top: 0px;
				}
			}
		}
	}
	tbody {
		tr {
			cursor: default;

			&.deleted {
				opacity: 0.2;
			}
			&.actionable {
				cursor: pointer;
			}
			background: ${props => props.theme.tableRowBackground};
			&:not(.noHover):hover {
				background: ${props => props.theme.tableRowHoverBackground};
			}
			&.alt {
				background: ${props => props.theme.tableRowAltBackground};
			}
			td {
				padding: 8px 12px;
				font-size: 14px;
				font-weight: 400;
				overflow: hidden;
				color: ${props => props.theme.tableCellTextColor};
				border-right: 1px solid ${props => props.theme.tableCellBorderColor};
				border-bottom: 1px solid ${props => props.theme.tableCellBorderColor};

				&.halign_right {
					text-align: right;
				}
				&.halign_center {
					text-align: center;
				}
				&.expansion {
					width: 1px;
					border-right: none;
					.icon {
						opacity: 1;
						&.disabled {
							visibility: hidden;
						}
					}
				}
				&.dummy {
					width: 1px;
					border-right: none;
				}
				&.subHeader {
					font-size: 14px;
					font-weight: 400;
					padding-left: 0px;
					background: ${props => props.theme.bcg1};
				}
				&.nodeTextCell {
					padding-left: 0px;
				}
			}
		}
	}
`;

const Filter = styled.div`
	margin-top: 8px;
	.ps-input {
		width: 100%;
		&[data-name=dummy] {
			visibility: hidden;
		}
	}
`;

const EMPTY_MAP = Immutable.Map({});
const EMPTY_LIST = Immutable.List([]);

/*
	renderer pro ResultSetModel z ps-gui (pouzito k zobrazeni vysledku v SearchModule)
*/
export default class PortalResultSetModule extends ParentBoundComponent {

	constructor() {
		super();

		this.exportToCsv = this.exportToCsv.bind(this);
	}

	exportToCsv() {
		exportToCsv(this.props.getSearchModel());
	}

	getModel() {
		return new ResultSetModel(this.getData());
	}

	setModel(model) {
		this.setData(model.getData());
	}

	setCustomFilterItem(name, value) {
		this.props.setCustomFilter((this.props.customFilter || EMPTY_MAP).set(name, value));
	}

	renderCols() {
		const data = this.getData();
		const columns = data.get('columns') || EMPTY_LIST;
		const defWidths = this.props.defaultColumnWidths || {};
		return (
			<colgroup>
				{
					columns.map((col, colIndex) => {
						const colName = col.get('name');
						return (
							<col
								key={colIndex}
								width={defWidths[colName] || '*'}
							/>
						);
					})
				}
			</colgroup>
		);
	}

	renderHeader() {
		const data = this.getData();
		// console.log(data.toJS());
		const columns = data.get('columns') || EMPTY_LIST;
		const customFilter = this.props.customFilter || EMPTY_MAP;
		const columnFilters = this.props.columnFilters || {};
		let rowCountLabel = this.props.rowCountLabel;
		if (rowCountLabel && typeof rowCountLabel === 'function') {
			rowCountLabel = rowCountLabel();
		}
		return (
			<thead>
				<tr className="sticky">
					{
						columns.map((col, colIndex) => {
							const sortable = col.get('sortable');
							const sortIndex = col.get('sortIndex') || 0;
							const colName = col.get('name');
							const text = col.get('text');
							const title = col.get('title');
							return (
								<th
									key={colIndex}
									className={sortable ? 'sortable' : ''}
								>
									<div
										className="text"
										onMouseDown={e => {
											e.preventDefault();
											if (sortable) {
												this.props.onSortRequested(col.get('name'), !e.ctrlKey);
											}
										}}
										title={title !== text ? title : null}
									>
										{text}
										{
											sortable &&
											<span className="sortIcons">
												{
													sortIndex >= 0 &&
													<Icon
														name="sortAsc"
														className={sortIndex > 0 ? 'selected' : ''}
													/>

												}
												{
													sortIndex <= 0 &&
													<Icon
														name="sortDesc"
														className={sortIndex < 0 ? 'selected' : ''}
													/>

												}
											</span>

										}
									</div>
									<Filter>
										<HeaderFilter
											value={customFilter.get(colName)}
											onChange={value => this.setCustomFilterItem(colName, value)}
											definition={columnFilters[colName]}
										/>
									</Filter>
								</th>
							);
						})
					}
				</tr>
				{
					this.props.renderSummaryHeader ? this.props.renderSummaryHeader(data) :
						<tr>
							<th colSpan={columns.size}>
								<FlexBox>
									<FlexBoxBlock flexGrow="1">
										<SummaryHeaderRowCount>
											<span className="label">{rowCountLabel || getPhrase('rowCount')}:</span>
											<span className="value">{data.getIn(['pager', 'totalCount'])}</span>
										</SummaryHeaderRowCount>
									</FlexBoxBlock>
									<FlexBoxBlock>
										{
											this.props.toolBar
										}
									</FlexBoxBlock>
								</FlexBox>
							</th>
						</tr>

				}
				<tr className="dummyHeaderRow">
					<th colSpan={columns.size}>
					</th>
				</tr>
			</thead>
		);
	}

	onRowAction(row, index, params) {
		if (typeof this.props.onRowAction === 'function') {
			this.props.onRowAction(row, index, params);
		}
	}

	renderRows() {
		const data = this.getData();
		const rows = data.get('rows') || EMPTY_LIST;
		let cellRenderers;
		const columns = data.get('columns') || EMPTY_LIST;
		const crm = this.props.cellRendererMap;
		if (crm) {
			cellRenderers = columns.map(col => {
				const colName = col.get('name');
				return crm[colName];
			});
		}
		let alignClasses;
		const columnAlign = this.props.columnAlign;
		if (columnAlign) {
			alignClasses = columns.map(col => {
				const align = columnAlign[col.get('name')];
				if (align) {
					return 'halign_' + align;
				} else {
					return null;
				}
			});
		}
		const actionable = this.props.onRowAction;
		return (
			<tbody>
				{
					rows.map((row, rowIndex) => {
						const cells = row.get('cells') || EMPTY_LIST;
						return (
							<tr
								key={rowIndex}
								className={(row.get('class') || '') + (rowIndex & 1 ? ' alt' : '') + (actionable ? ' actionable' : '')}
								onClick={e => {
									this.onRowAction(row, rowIndex, {
										ctrlKey: e.ctrlKey,
										altKey: e.altKey
									});
								}}
							>
								{
									cells.map((cell, colIndex) => {
										let value = cell;
										if (cellRenderers) {
											const cr = cellRenderers.get(colIndex);
											if (cr) {
												value = cr(cell, row, colIndex);
											}
										}
										return (
											<td key={colIndex} className={alignClasses ? alignClasses.get(colIndex) : null}>
												{value}
											</td>
										);
									})
								}
							</tr>
						);
					})
				}
			</tbody>
		);

	}

	setMaxRowsPerPage(maxRowsPerPage) {
		/*const data = this.getData();
		let firstRow = data.getIn(['pager', 'firstRow']) || 1;
		firstRow = Math.floor((firstRow - 1) / maxRowsPerPage) * maxRowsPerPage + 1;*/
		this.props.onPageRequested(1, maxRowsPerPage);
	}

	renderRowsPerPageSelection() {
		const data = this.getData();
		const maxRowsPerPage = data.getIn(['pager', 'maxRowsPerPage']);
		return (
			<RowsPerPageSelection>
				<span className="label">{getPhrase('rowsPerPage')}:</span>
				{
					rowPerPageOptions.map(rpp => {
						const selected = rpp === maxRowsPerPage;
						return (
							<span
								key={rpp}
								className={'value' + (selected ? ' selected' : '')}
								onMouseDown={e => {
									e.preventDefault();
									if (!selected) {
										this.setMaxRowsPerPage(rpp);
									}
								}}
							>{rpp}</span>
						);
					})
				}
			</RowsPerPageSelection>
		);
	}

	render() {
		const data = this.getData();
		//  console.log('prsm = ', data.toJS());
		return (
			<Wrapper>
				<SmartTable>
					{this.renderCols()}
					{this.renderHeader()}
					{this.renderRows()}
				</SmartTable>
				{this.renderRowsPerPageSelection()}
				<FlexBox margin="32px 0px" alignItems="center">
					<FlexBoxBlock flexGrow="1">
						<Pager
							pager={data.get('pager')}
							prevButtonText={getPhrase('previous')}
							nextButtonText={getPhrase('next')}
							spread={1}
							onPageRequested={this.props.onPageRequested}
						/>
					</FlexBoxBlock>
					<FlexBoxBlock>
						{
							this.props.withNewEntityButton &&
							<Button
								text={getPhrase('new.' + data.get('entityName'))}
								onAction={this.props.onNewEntityRequested}
							/>

						}
					</FlexBoxBlock>
				</FlexBox>
			</Wrapper>
		);
	}
}

const rowPerPageOptions = [
	16,
	32,
	48
];

function HeaderFilter({
	value,
	onChange,
	definition
}) {
	if (!definition) {
		return (
			<TextInput
				value=''
				readOnly
				name="dummy"
			/>
		);
	}
	const type = definition.type;
	if (type === 'lookup') {
		const lu = getLookup(definition.lookup);
		return <LookupInput
			getRows={lu.getRows}
			getText={lu.getText}
			value={value}
			onChange={onChange}
			combo={lu.supportsCombo}
		/>;
	} else if (type === 'text') {
		return <TextInput2
			value={value}
			onChange={onChange}
			changeOnBlur
			withClearButton
			rightIcon="search"
		/>;
	} else if (type === 'runtime') {
		return <PopupInput
			value={value}
			text={(value || EMPTY_MAP).get('text')}
			onChange={onChange}
			popupControl={RuntimeRangeInput}
			popupControlProps={{
				inputSize: definition.inputSize || 4,
				placeholderFrom: getColumnText('from'),
				placeholderTo: getColumnText('to')
			}}
		/>;
	} else if (type === 'number') {
		return <PopupInput
			value={value}
			text={(value || EMPTY_MAP).get('text')}
			onChange={onChange}
			popupControl={NumberRangeInput}
			popupControlProps={{
				inputSize: definition.inputSize,
				groupless: definition.groupless,
				placeholderFrom: getColumnText('from'),
				placeholderTo: getColumnText('to')
			}}
		/>;
	} else if (type === 'date') {
		return <PopupInput
			value={value}
			text={(value || EMPTY_MAP).get('text')}
			onChange={onChange}
			popupControl={DateRangeInput}
			popupControlProps={{
				minWidth: definition.withTime ? '380px' : '280px',//definition.inputSize,
				placeholderFrom: getColumnText('from'),
				placeholderTo: getColumnText('to'),
				withTime: definition.withTime
			}}
		/>;
	} else if (type === 'custom') {
		return <PopupInput
			value={value}
			text={definition.getText(value)}
			onChange={onChange}
			popupControl={definition.control}
			popupControlProps={definition.controlProps}
			maxPopupHeight={definition.maxPopupHeight}
		/>;
	}
	return 'filter - ' + definition.type;
}
